/**
 * La guida a due assi: canali in colonna, tempo in ascissa.
 *
 * Le misure sono quelle del prototipo v3 (03-SCHERMATE-TV §5): finestra 20:30–23:00,
 * 6,6 pixel al minuto, tacche ogni 30 minuti, righe alte 88 px con 10 px di stacco.
 * Qui non c'è niente che disegni: ci sono i conti, perché sono gli stessi su ogni
 * piattaforma e vanno provati senza un browser.
 */

import { livenessOf, progressPercent, type Liveness, type TimeWindow } from './liveness.js';

/**
 * Quanti pixel vale un minuto sulla riga del tempo.
 *
 * Era 6,6 — il valore del prototipo — e su 150 minuti dava 990 px. Sceso a 5,6 per **restituire
 * 150 px alle due colonne di sinistra**: i nomi delle categorie e dei canali erano tagliati, e sono
 * l'informazione per cui si apre la guida. Un'ora un po' più stretta si legge lo stesso; un canale
 * che si chiama «Sky Sport Uno H…» non si riconosce.
 */
export const PIXELS_PER_MINUTE = 5.6;
export const ROW_HEIGHT_PX = 88;
export const ROW_GAP_PX = 10;
export const ROW_PITCH_PX = ROW_HEIGHT_PX + ROW_GAP_PX;
/** Lo stacco fra un programma e il successivo, tolto dalla larghezza (prototipo: `- 6`). */
export const PROGRAM_GAP_PX = 6;
export const TIME_SLOT_MINUTES = 30;

export interface Channel {
  readonly id: string;
  /** Il numero che l'utente vede e digita. */
  readonly number: number;
  readonly name: string;
  /**
   * Il logo dell'emittente, o `null` quando il catalogo non ne dichiara uno.
   *
   * ## Il dato c'era e nessuno lo portava
   *
   * Il livello compatibile manda `stream_icon` da sempre — su stage ce l'hanno **33 canali su 61** —
   * e `parseChannels` lo scartava. La guida disegnava le iniziali su ogni riga, quindi non sembrava
   * un difetto: sembrava una scelta di disegno. È la stessa forma del programma che nessuno mostrava
   * e dei promemoria che nessuno leggeva.
   *
   * `null` e non stringa vuota: «non c'è» e «c'è ed è vuoto» sono la stessa cosa per chi disegna, ma
   * solo il primo dei due si può controllare con un tipo.
   */
  readonly logoUrl: string | null;
  readonly categoryId: string;
  /** `tv_archive` del livello compatibile: il canale ha l'archivio. */
  readonly hasArchive: boolean;
  /**
   * Per quante **ore** indietro si può rivedere. Zero se non ha archivio.
   *
   * ## Perché ore e non giorni
   *
   * Xtream esprime `tv_archive_duration` in giorni, e un archivio di due ore lì vale zero: per un
   * lettore di terzi è la verità — un giorno intero non ce l'ha — ma per noi significava buttare via
   * un archivio che esiste. Un canale con due ore di catchup è utilissimo: è quello che serve per
   * riprendere la partita dall'inizio.
   *
   * Quindi il campo condiviso resta in giorni per chi legge Xtream, e il nostro lettore usa
   * `tv_archive_hours`, che il backend aggiunge accanto. **Qui dentro l'unità è una sola**, perché
   * due unità che si convertono sono due posti in cui sbagliare un fattore 24 — e un fattore 24
   * sbagliato non somiglia a un errore di unità, somiglia a un archivio che non c'è.
   */
  readonly archiveHours: number;
  /** `is_adult`, già risolto dal backend con l'eredità dal bouquet. */
  readonly isAdult: boolean;
  /**
   * Di questo canale **esiste davvero un palinsesto** (`has_epg`).
   *
   * Non «dichiara una mappatura EPG»: quella non dice niente. Il listino ripiega su
   * l'identificativo del canale quando la mappatura manca, quindi ogni canale sembra mappato —
   * misurato su un catalogo cliente il 30-09-2026: 11.212 su 11.212 dichiaravano un
   * `epg_channel_id`, e chiedendone 200 tornavano 17 programmi su **4** canali.
   *
   * Serve al giro di fondo della guida, che chiedeva il palinsesto di tutti: 57 richieste l'ora per
   * scaricare quel che sta in due. Quali canali ne abbiano uno lo sa solo il database, e questo campo
   * è la sua risposta.
   *
   * Falso su un gateway più vecchio del campo — e lì il giro torna a chiederli tutti, che è quel che
   * faceva prima: si perde l'economia, non la guida.
   */
  readonly hasEpg: boolean;
}

export interface Program {
  readonly id: string;
  readonly channelId: string;
  readonly title: string;
  readonly genre: string;
  readonly startMs: number;
  readonly endMs: number;
  /** Tipologia evento (`calcio`, `tennis`, …) quando c'è. Il prototipo la chiama `k`. */
  readonly eventKind: string | null;
  readonly description: string;
}

/**
 * La finestra di tempo visibile nella griglia.
 *
 * Un nome, non una forma nuova: è un `TimeWindow` e non aggiunge niente. Era scritto come
 * `interface GuideWindow extends TimeWindow {}`, che è la stessa cosa detta in un modo che fa
 * credere a chi legge che ci sia un campo in più.
 */
export type GuideWindow = TimeWindow;

export function windowFromStart(startMs: number, minutes: number): GuideWindow {
  return { startMs, endMs: startMs + minutes * 60_000 };
}

/**
 * La finestra che il giro di fondo scarica, **ancorata all'ora tonda precedente**.
 *
 * Non è la finestra che si vede: quella segue la mezz'ora in corso e si muove quando si scorre. Questa
 * è quel che si chiede al gateway, e sono due cose diverse per due ragioni.
 *
 * La prima è che il giro **non deve dipendere da dove si sta guardando**. Quando dipendeva, ripartiva
 * da capo a ogni mezz'ora e l'intervallo di un'ora non è mai arrivato a scadere: un'ora dichiarata e
 * mezz'ora in atto, senza che niente protestasse.
 *
 * La seconda è la cache del backend, la cui chiave comprende gli estremi della finestra. Con l'istante
 * esatto ogni apparecchio avrebbe una chiave sua e la cache non prenderebbe mai; ancorata all'ora,
 * tutti gli apparecchi accesi nella stessa ora chiedono gli stessi estremi e pagano una query in uno.
 *
 * L'ora **precedente** e non quella in corso: alle 20:05 la finestra parte dalle 19:00, così il
 * programma iniziato alle 19:40 c'è per intero invece di cominciare tagliato.
 *
 * Un'ora di margine oltre la larghezza visibile: entro il giro dopo la coda è già coperta. Quel che
 * sta oltre resta vuoto, che è la regola della guida e non una mancanza.
 */
export function guideRefreshWindow(nowMs: number, visibleMinutes: number): GuideWindow {
  const hour = 60 * 60_000;
  const startMs = Math.floor(nowMs / hour) * hour - hour;
  return { startMs, endMs: startMs + (visibleMinutes + 60) * 60_000 };
}

/** Posizione in pixel di un istante dentro la finestra. */
export function timeToPx(ms: number, span: GuideWindow, pixelsPerMinute = PIXELS_PER_MINUTE): number {
  return ((ms - span.startMs) / 60_000) * pixelsPerMinute;
}

export function windowWidthPx(span: GuideWindow, pixelsPerMinute = PIXELS_PER_MINUTE): number {
  return timeToPx(span.endMs, span, pixelsPerMinute);
}

export function overlapsWindow(program: Program, span: GuideWindow): boolean {
  // Prototipo v3: `p.e > W0 && p.s < W1`.
  return program.endMs > span.startMs && program.startMs < span.endMs;
}

export interface ProgramLayout {
  readonly leftPx: number;
  readonly widthPx: number;
  /**
   * Il programma era già cominciato quando la finestra si apre: va mostrato tagliato,
   * con un `‹` davanti al titolo. Chi arriva a metà lo capisce (03-SCHERMATE-TV §5).
   */
  readonly clippedAtStart: boolean;
  readonly clippedAtEnd: boolean;
}

export function layoutProgram(
  program: Program,
  span: GuideWindow,
  pixelsPerMinute = PIXELS_PER_MINUTE,
): ProgramLayout {
  const from = Math.max(program.startMs, span.startMs);
  const to = Math.min(program.endMs, span.endMs);
  const leftPx = timeToPx(from, span, pixelsPerMinute);
  const rawWidth = timeToPx(to, span, pixelsPerMinute) - leftPx - PROGRAM_GAP_PX;
  return {
    leftPx,
    widthPx: Math.max(0, rawWidth),
    clippedAtStart: program.startMs < span.startMs,
    clippedAtEnd: program.endMs > span.endMs,
  };
}

/** Le tacche degli orari, ogni 30 minuti, allineate al mezz'ora tonda. */
export function timeSlots(
  span: GuideWindow,
  pixelsPerMinute = PIXELS_PER_MINUTE,
  slotMinutes = TIME_SLOT_MINUTES,
): ReadonlyArray<{ atMs: number; leftPx: number }> {
  const step = slotMinutes * 60_000;
  const first = Math.ceil(span.startMs / step) * step;
  const slots: Array<{ atMs: number; leftPx: number }> = [];
  for (let at = first; at < span.endMs; at += step) {
    slots.push({ atMs: at, leftPx: timeToPx(at, span, pixelsPerMinute) });
  }
  return slots;
}

export function programLiveness(program: Program, serverNowMs: number): Liveness {
  return livenessOf({ startMs: program.startMs, endMs: program.endMs }, serverNowMs);
}

export function programProgressPercent(program: Program, serverNowMs: number): number {
  return progressPercent({ startMs: program.startMs, endMs: program.endMs }, serverNowMs);
}

/**
 * Cosa succede premendo OK su questo programma. È la soluzione al problema vero di una
 * guida: lo stesso tasto fa tre cose diverse a seconda di dove sei, e dirlo prima costa
 * una riga di testo (03-SCHERMATE-TV §5).
 */
export type GuideAction =
  | { readonly kind: 'watch' }
  | { readonly kind: 'replay' }
  | { readonly kind: 'setReminder' }
  | { readonly kind: 'reminderSet' }
  | { readonly kind: 'unavailable' };

export function guideAction(
  program: Program,
  channel: Channel,
  serverNowMs: number,
  reminderSet: boolean,
): GuideAction {
  switch (programLiveness(program, serverNowMs)) {
    case 'live':
      return { kind: 'watch' };
    case 'future':
      return reminderSet ? { kind: 'reminderSet' } : { kind: 'setReminder' };
    case 'past':
      return isReplayable(program, channel, serverNowMs) ? { kind: 'replay' } : { kind: 'unavailable' };
  }
}

/**
 * Un programma già passato è riproducibile se il suo canale ha l'archivio **e** se cade
 * dentro la finestra disponibile (06-RIPRODUZIONE §8bis). Se non lo è, non è cliccabile
 * e si vede che non lo è: niente OK che non fa niente.
 */
export function isReplayable(program: Program, channel: Channel, serverNowMs: number): boolean {
  if (!channel.hasArchive || channel.archiveHours <= 0) return false;
  if (program.endMs > serverNowMs) return false;
  const oldestAvailableMs = serverNowMs - channel.archiveHours * 60 * 60_000;
  return program.startMs >= oldestAvailableMs;
}

/**
 * I canali di cui vale la pena chiedere il palinsesto.
 *
 * ## Perché non basta filtrare su `hasEpg`
 *
 * Perché il campo è nuovo. Un gateway che non lo conosce non lo manda, `hasEpg` risulta falso su
 * tutti, e un filtro ingenuo non chiederebbe la guida di **nessuno**: aggiornare l'app prima del
 * backend spegnerebbe la guida invece di alleggerirla. È il verso di guasto peggiore, perché
 * l'economia riuscirebbe alla perfezione.
 *
 * Quindi la regola è sull'elenco intero e non sul singolo canale: se **nessuno** dichiara di avere un
 * palinsesto, la risposta non è «non ne ha nessuno», è «questo gateway non sa rispondere» — e allora
 * si torna a chiederli tutti, che è quel che si faceva prima. Si perde l'economia, non la guida.
 *
 * Il caso legittimo di zero canali con guida — un catalogo senza nessun EPG ingerito — costa un giro
 * a vuoto ogni ora. È il prezzo per non poter distinguere «nessuno» da «non lo so», ed è il verso
 * giusto in cui sbagliare.
 *
 * ## Quanto vale
 *
 * Misurato il 30-09-2026 su un catalogo cliente: 11.212 canali, di cui una manciata con un
 * palinsesto vero. Il giro di fondo faceva **57 richieste l'ora** per scaricare quel che sta in due.
 */
export function channelsWorthAsking(
  channels: ReadonlyArray<Channel>,
): ReadonlyArray<Channel> {
  const withGuide = channels.filter((channel) => channel.hasEpg);
  return withGuide.length === 0 ? channels : withGuide;
}
